// The signed-in user's CalDAV connection: their server and user name, their
// CalDAV calendars and the role each has, and the actions the caldav edge
// function takes for them (connect, sync, disconnect). The password and any
// Access service token go to the server once, when connecting, and never
// come back.

import type { CalendarRole } from '@cerebro-diem/core';
import { supabase } from './supabase';
import { invokeEdgeFunction } from './edgeFunctions';
import { isRecord } from './errors';

export interface CaldavConnection {
  server_url: string;
  username: string;
  needs_reconnect: boolean;
  last_error: string | null;
  connected_at: string;
  last_sync_at: string | null;
}

export interface CaldavCalendar {
  id: string;
  name: string;
  color: string | null;
  is_default: boolean;
  role: CalendarRole;
}

export interface CaldavConnectInput {
  server_url: string;
  username: string;
  password: string;
  /** A Cloudflare Access service token, for a server behind Access; both or neither. */
  access_client_id?: string;
  access_client_secret?: string;
}

/** CalDAV's queries live under the calendar root, so a calendar invalidation refreshes them too. */
export const caldavKeys = {
  all: ['calendar', 'caldav'] as const,
  connection: ['calendar', 'caldav', 'connection'] as const,
  calendars: ['calendar', 'caldav', 'calendars'] as const,
};

/** The user's connection, or null when they have not connected. */
export async function caldavConnection(): Promise<CaldavConnection | null> {
  const { data, error } = await supabase
    .from('caldav_connections')
    .select('server_url, username, needs_reconnect, last_error, connected_at, last_sync_at')
    .maybeSingle();
  if (error) throw error;
  return (data ?? null) as CaldavConnection | null;
}

export async function caldavCalendars(): Promise<CaldavCalendar[]> {
  const { data, error } = await supabase.from('calendars').select('id, name, color, is_default, role').eq('source', 'caldav').order('name');
  if (error) throw error;
  return (data ?? []) as CaldavCalendar[];
}

/** Check the server and credentials, keep them, and list the server's calendars (all starting off). */
export async function connectCaldav(input: CaldavConnectInput): Promise<{ calendars: number }> {
  const result = await invokeEdgeFunction('caldav', { action: 'connect', ...input });
  if (!isRecord(result) || typeof result.calendars !== 'number') {
    throw new Error('The server answered the connection with something unexpected');
  }
  return { calendars: result.calendars };
}

export async function syncCaldav(): Promise<{ calendars: number; events: number }> {
  const result = await invokeEdgeFunction('caldav', { action: 'sync' });
  if (!isRecord(result) || typeof result.calendars !== 'number' || typeof result.events !== 'number') {
    throw new Error('The server answered the sync with something unexpected');
  }
  return { calendars: result.calendars, events: result.events };
}

export async function disconnectCaldav(): Promise<void> {
  await invokeEdgeFunction('caldav', { action: 'disconnect' });
}
